import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { messageAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import MessageThread from '../components/common/MessageThread';

const ClientMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [arrowHovered, setArrowHovered] = useState(null);
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const dateLocale = language === 'uz' ? 'uz-UZ' : language === 'ru' ? 'ru-RU' : 'en-US';

  useEffect(() => {
    messageAPI.getSent()
      .then(res => setMessages(res.data || []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, []);

  const handleExpand = (id) => {
    const opening = expanded !== id;
    setExpanded(opening ? id : null);
    if (opening) {
      const msg = messages.find(m => m.id === id);
      if (msg?.new_reply_for_client) {
        messageAPI.clientRead(id).catch(() => {});
        setMessages(prev => prev.map(m => m.id === id ? { ...m, new_reply_for_client: false } : m));
      }
    }
  };

  if (loading) return <div style={s.center}>{t('messages.loading')}</div>;

  return (
    <div style={s.page}>
      <div style={s.container}>
        <h1 style={s.title}>{t('messages.myMessagesTitle')}</h1>

        {messages.length === 0 ? (
          <div style={s.empty}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1rem' }}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <p style={s.emptyText}>{t('messages.noSentMessages')}</p>
            <button style={s.btn} onClick={() => navigate('/find-attorney')}>{t('messages.findAttorneyBtn')}</button>
          </div>
        ) : (
          <div style={s.list}>
            {messages.map(msg => (
              <div key={msg.id} style={s.card} onClick={() => handleExpand(msg.id)}>
                <div style={s.cardTop}>
                  <div>
                    <span style={s.toLabel}>{t('messages.toLabel')}</span>
                    <span style={s.toName}>{msg.attorney_name}</span>
                    {msg.attorney_location && <span style={s.location}> · {msg.attorney_location}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {msg.new_reply_for_client && (
                      <span style={s.replyBadge}>{t('messages.newReplyBadge')}</span>
                    )}
                    <div
                      onMouseEnter={() => setArrowHovered(msg.id)}
                      onMouseLeave={() => setArrowHovered(null)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.35rem',
                        padding: '0.2rem 0.45rem', borderRadius: '5px',
                        backgroundColor: arrowHovered === msg.id ? '#ebf8ff' : 'transparent',
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <span style={{ ...s.date, color: arrowHovered === msg.id ? '#3182ce' : undefined }}>
                        {new Date(msg.created_at).toLocaleDateString(dateLocale)}
                      </span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke={arrowHovered === msg.id ? '#3182ce' : '#a0aec0'}
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        style={{ transform: expanded === msg.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'block' }}>
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <p style={{ ...s.situation, WebkitLineClamp: expanded === msg.id ? 'unset' : 2, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: expanded === msg.id ? 'visible' : 'hidden' }}>
                  {msg.situation}
                </p>
                {msg.phone_preferred && (
                  <div style={s.phoneBadge}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.3rem' }}>
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6 6l.86-.86a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    {t('messages.phonePreferredClient')} {msg.phone_number ? `· ${msg.phone_number}` : ''} {msg.preferred_time ? `· ${msg.preferred_time}` : ''}
                  </div>
                )}
                {expanded === msg.id && (
                  <div onClick={e => e.stopPropagation()}>
                    <MessageThread messageId={msg.id} currentUserType="client" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { backgroundColor: '#f7fafc', minHeight: 'calc(100vh - 120px)', padding: '3rem 2rem' },
  container: { maxWidth: '800px', margin: '0 auto' },
  title: { fontSize: '1.8rem', fontWeight: '800', color: '#1a365d', marginBottom: '2rem' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: '#718096' },
  empty: { textAlign: 'center', padding: '4rem 2rem', backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  emptyText: { color: '#718096', marginBottom: '1.5rem' },
  list: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', cursor: 'pointer' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' },
  toLabel: { fontSize: '0.82rem', color: '#a0aec0', marginRight: '0.4rem' },
  toName: { fontWeight: '700', color: '#1a365d', fontSize: '0.95rem' },
  location: { color: '#718096', fontSize: '0.88rem' },
  date: { fontSize: '0.8rem', color: '#a0aec0' },
  situation: { color: '#4a5568', fontSize: '0.9rem', lineHeight: '1.6' },
  phoneBadge: { display: 'flex', alignItems: 'center', marginTop: '0.75rem', fontSize: '0.82rem', color: '#2b6cb0', backgroundColor: '#ebf8ff', padding: '0.3rem 0.75rem', borderRadius: '20px', width: 'fit-content' },
  btn: { backgroundColor: '#1a365d', color: 'white', border: 'none', padding: '0.75rem 1.75rem', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' },
  replyBadge: { fontSize: '0.72rem', fontWeight: '700', color: 'white', backgroundColor: '#e53e3e', padding: '0.15rem 0.55rem', borderRadius: '20px', whiteSpace: 'nowrap' },
};

export default ClientMessages;
